import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { formatDate, formatDateTime } from "../../../utils";

// Status codes for which the "Relevant date" shows planned_for/finished_at
// instead of the default status_changed_at (see JobStatus in
// backend/service/consts.py, and job_timeline_date in service/serializers.py -
// the backend picks the date, this only labels it).
const PENDING_STATUS = "P";
const FINISHED_STATUS = "F";
const TIMELINE_LABEL_BY_STATUS: Record<string, string> = {
  [PENDING_STATUS]: "Planned for",
  [FINISHED_STATUS]: "Finished at",
};
const DEFAULT_TIMELINE_LABEL = "Status changed at";

/** Shared date(+time) styling so every date column lines up on the same left
 * margin - bold date on top, smaller secondary-colored time below. */
export const DateTimeLines = ({ date, time }: { date: string; time?: string }) => (
  <>
    <Typography sx={{ fontWeight: 600, fontSize: 12, lineHeight: 1.15 }}>{date}</Typography>
    {time && (
      <Typography sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1.15 }}>
        {time}
      </Typography>
    )}
  </>
);

/** The "Relevant date" cell: one date per job, labelled by what it means at the
 * job's current stage. Shared by the jobs list, the statistics job breakdowns
 * and the equipment service-history tables so the same job never reads as a
 * different date depending on which table it is looked at from. */
export default function RelevantDateCell({
  value,
  status,
}: {
  value?: string | null;
  status?: string;
}) {
  const { t } = useTranslation();

  if (!value) {
    return <>-</>;
  }

  const labelKey = (status && TIMELINE_LABEL_BY_STATUS[status]) || DEFAULT_TIMELINE_LABEL;
  const isPending = status === PENDING_STATUS;
  const [datePart, timePart] = isPending
    ? [formatDate(value), undefined]
    : formatDateTime(value).split("\n");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15 }}>
        {t(labelKey)}
      </Typography>
      <DateTimeLines date={datePart} time={timePart} />
    </Box>
  );
}
